import { Form, Input, Modal } from "antd";
import { WorkOrderLog, WorkOrderLogForm } from "../../../store/data/entities/work-order-log";
import { useAddWorkOrderLogMutation } from "../../../store/services/api/work-order-api";
import UserSelect from "../../forms/controls/UserSelect";
import { UserRole } from "../../../store/data/users/user-roles";
import { useEffect, useRef, useState } from "react";
import useUser from "../../../hooks/useUser";

interface AddWorkLogModalProps {
  workOrderId: string;
  previousWorkOrderLog?: WorkOrderLog;
  defaultUserId?: string;
  userRoles?: UserRole[];
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export default function AddWorkLogModal(props: AddWorkLogModalProps) {
  const [form] = Form.useForm<WorkOrderLogForm>();
  const [addWorkLog, { isLoading }] = useAddWorkOrderLogMutation();
  const { user } = useUser();
  const isExternal = user.role === "ExternalTechnician";
  const isEdit = !!props.previousWorkOrderLog;

  const [position, setPosition] = useState({ x: 0, y: 0 });
  const dragData = useRef<{ startX: number; startY: number; initialX: number; initialY: number } | null>(null);

  useEffect(() => {
    if (props.open) {
      form.resetFields();
      const modalWidth = Math.min(600, window.innerWidth - 24);
      const modalHeight = 300;
      const x = Math.max((window.innerWidth - modalWidth) / 2, 8);
      const y = Math.max((window.innerHeight - modalHeight) / 3, 8);
      setPosition({ x, y });
    }
  }, [props.open]);

  const handleOnFinish = async (data: WorkOrderLogForm) => {
    const originalLogEntryId =
      props.previousWorkOrderLog?.originalLogEntryId ?? props.previousWorkOrderLog?.id;

    await addWorkLog({
      ...data,
      workOrderId: props.workOrderId,
      originalLogEntryId: isEdit ? originalLogEntryId : undefined,
    }).unwrap();

    props.onSuccess?.();
  };

  const handleDragStart = (e: React.MouseEvent | React.TouchEvent) => {
    const clientX = "touches" in e ? e.touches[0].clientX : e.clientX;
    const clientY = "touches" in e ? e.touches[0].clientY : e.clientY;

    dragData.current = {
      startX: clientX,
      startY: clientY,
      initialX: position.x,
      initialY: position.y,
    };

    document.addEventListener("mousemove", handleDragging);
    document.addEventListener("touchmove", handleDragging as any, { passive: false });
    document.addEventListener("mouseup", handleDragEnd);
    document.addEventListener("touchend", handleDragEnd);
  };

  const handleDragging = (e: MouseEvent | TouchEvent) => {
    if (!dragData.current) return;

    const clientX = "touches" in e ? (e as TouchEvent).touches[0].clientX : (e as MouseEvent).clientX;
    const clientY = "touches" in e ? (e as TouchEvent).touches[0].clientY : (e as MouseEvent).clientY;

    const dx = clientX - dragData.current.startX;
    const dy = clientY - dragData.current.startY;

    setPosition({
      x: dragData.current.initialX + dx,
      y: dragData.current.initialY + dy,
    });

    e.preventDefault(); // prevent scroll while dragging
  };

  const handleDragEnd = () => {
    document.removeEventListener("mousemove", handleDragging);
    document.removeEventListener("touchmove", handleDragging as any);
    document.removeEventListener("mouseup", handleDragEnd);
    document.removeEventListener("touchend", handleDragEnd);
    dragData.current = null;
  };

  return (
    <Modal
      open={props.open}
      title={
        <div
          onMouseDown={handleDragStart}
          onTouchStart={handleDragStart}
          style={{
            cursor: "move",
            userSelect: "none",
            padding: "4px 0",
            fontWeight: 600,
          }}
        >
          {isEdit ? "Update Work Log" : "Add Work Log"}
        </div>
      }
      loading={isLoading}
      onCancel={props.onClose}
      onOk={() => form.submit()}
      destroyOnClose
      centered={false}
      width={Math.min(600, window.innerWidth - 24)}
      style={{
        position: "absolute",
        top: position.y,
        left: position.x,
        transform: "none",
        margin: 0,
      }}
    >
      <Form<WorkOrderLogForm>
        form={form}
        layout="vertical"
        onFinish={handleOnFinish}
        initialValues={{
          userId: props.defaultUserId,
          comments: props.previousWorkOrderLog?.comments,
        }}
      >
        <Form.Item name="userId" label="User" rules={[{ required: true }]}>
          <UserSelect disabled={isEdit || isExternal} userRoles={props.userRoles} />
        </Form.Item>

        <Form.Item name="comments" label="Comments">
          <Input.TextArea autoSize={{ minRows: 3, maxRows: 6 }} />
        </Form.Item>
      </Form>
    </Modal>
  );
}
